import React from "react";
import Gutter from "../../../../ui-kit/Gutter";
import ChatMessage, {
  ChatContainer,
} from "../../../../ui-kit/ChatMessage/ChatMessage";
import { useWebTranslation } from "../../../../translations";
import { Typography } from "../../../../ui-kit/Typography";
import useBreeds from "../../../../hooks/useBreeds";
import { MessageButton } from "components/ContentfulRenderer/ContentfulC2A/MessageButton";
import { StepProps } from "../types";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { updateAnimalById } from "components/Signup/store/slices/animals";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import { SelectorSearch } from "components/Signup/components/SelectorSearch";
import { BreedEmoji } from "components/BreedEmoji";
import { MinimalBreedInfo } from "components/Signup/store/state/types";
import { isOrg } from "utils/site";

// https://lassieco.slack.com/archives/C019CN57ZGW/p1732721513722039
const excludedMixedBreeds = [
  "DogBreed-aussiedoodle",
  "DogBreed-australian-cobberdog",
  "DogBreed-australian-labradoodle",
  "DogBreed-cavapoo",
  "DogBreed-cobberdog",
  "DogBreed-cockerpoo",
  "DogBreed-goldendoodle",
  "DogBreed-labradoodle",
  "DogBreed-lurcher",
  "DogBreed-mal-shi",
  "DogBreed-maltipoo",
  "DogBreed-pomchi",
  "DogBreed-schabrador",
  "DogBreed-schnoodle",
  "DogBreed-shih-poo",
  "DogBreed-yorkiepoo",
];

const AnimalMixedBreed = ({
  previous,
  next,
  isValid,
  transitioning,
}: StepProps) => {
  const { t } = useWebTranslation();

  const { species, mixedBreedFHalf, mixedBreedSHalf, id } = useCurrentAnimal();
  const dispatch = useSignupDispatch();

  const { breeds: firstBreeds, breedsLoading: firstBreedsLoading } = useBreeds({
    species: species ?? null,
    excludeBreeds: `
      ${mixedBreedSHalf?.id},
      DogBreed-blandras,
      DogBreed-mixed-breed-high-risk, DogBreed-mixed-breed-low-risk"
      ${isOrg("swe") ? `,${excludedMixedBreeds.join(",")}` : ""}
    `,
  });

  const { breeds: secondBreeds, breedsLoading: secondBreedsLoading } =
    useBreeds({
      species: species ?? null,
      excludeBreeds: `
      ${mixedBreedFHalf?.id},
      DogBreed-blandras,
      DogBreed-mixed-breed-high-risk
      ${isOrg("swe") ? `,${excludedMixedBreeds.join(",")}` : ""}
    `,
      topBreeds: "DogBreed-mixed-breed-low-risk",
    });

  return (
    <ChatContainer leaving={transitioning}>
      <ChatMessage>
        <Typography>{t("signup.animal_mixed_breed.text_1")}</Typography>
      </ChatMessage>
      <Gutter size={2} />
      <ChatMessage fromMe small>
        <SelectorSearch<MinimalBreedInfo>
          items={firstBreeds}
          onSelectedItemChange={({ selectedItem }) =>
            dispatch(
              updateAnimalById({
                id,
                update: { mixedBreedFHalf: selectedItem },
              })
            )
          }
          placeholder={t("signup.animal_mixed_breed.breed_selector_1")}
          defaultSelectedItem={mixedBreedFHalf}
          icon={<BreedEmoji />}
          itemToString={(item) => item?.name ?? ""}
          label={t("a11y.signup.breed.label")}
        />
      </ChatMessage>
      <Gutter size={2} />
      <ChatMessage>
        <Typography>{t("signup.animal_mixed_breed.text_2")}</Typography>
        <Gutter size={1.5} />
        <Typography color="grey500">
          {t("signup.animal_mixed_breed.description_2", {
            species: species ?? "",
          })}
        </Typography>
      </ChatMessage>
      <Gutter size={2} />
      <ChatMessage fromMe small>
        <SelectorSearch<MinimalBreedInfo>
          items={secondBreeds}
          onSelectedItemChange={({ selectedItem }) =>
            dispatch(
              updateAnimalById({
                id,
                update: { mixedBreedSHalf: selectedItem },
              })
            )
          }
          placeholder={t("signup.animal_mixed_breed.breed_selector_2")}
          defaultSelectedItem={mixedBreedSHalf}
          icon={<BreedEmoji />}
          itemToString={(item) => item?.name ?? ""}
          label={t("a11y.signup.breed.label")}
        />
      </ChatMessage>
      <Gutter size={2} />
      <ChatMessage>
        <Typography>{t("signup.animal_mixed_breed.text_3")}</Typography>
        <Gutter size={2} />
        <MessageButton />
      </ChatMessage>
      <FlowButtons
        inProgress={transitioning}
        next={next}
        disabled={!isValid}
        previous={previous}
        backButton={false}
      />
    </ChatContainer>
  );
};

export default AnimalMixedBreed;
